import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useNotify, useTranslate } from 'react-admin';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
} from '@mui/material';
import { Close as CloseIcon, Save as SaveIcon } from '@mui/icons-material';
import { PasswordField } from '@/components/forms';
import { extractErrorMessage } from '@/utils/extractErrorMessage';
import { updateUserPassword } from '../services/userProfileService';
import { PASSWORD_MIN_LENGTH, PASSWORD_PATTERN } from '../constants';
import type { PasswordResetFormValues } from '../types/userProfile';

interface PasswordResetDialogProps {
  open: boolean;
  onClose: () => void;
}

const emptyForm: PasswordResetFormValues = {
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
};

export const PasswordResetDialog: React.FC<PasswordResetDialogProps> = ({
  open,
  onClose,
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [submitting, setSubmitting] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isValid },
  } = useForm<PasswordResetFormValues>({
    defaultValues: emptyForm,
    mode: 'all',
    reValidateMode: 'onChange',
    criteriaMode: 'all',
    shouldFocusError: true,
  });

  const newPassword = watch('newPassword');

  const handleClose = (): void => {
    reset(emptyForm);
    onClose();
  };

  const onSubmit = async (values: PasswordResetFormValues): Promise<void> => {
    setSubmitting(true);
    try {
      await updateUserPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      notify('user-profile.notifications.passwordUpdated', { type: 'success' });
      handleClose();
    } catch (error: unknown) {
      notify(
        extractErrorMessage(
          error,
          'user-profile.notifications.passwordFailed',
          translate
        ),
        { type: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {translate('user-profile.password.title')}
        <IconButton onClick={handleClose} size="small" aria-label="close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Controller
              name="currentPassword"
              control={control}
              rules={{
                required: translate('user-profile.password.errors.currentRequired'),
              }}
              render={({ field }) => (
                <PasswordField
                  {...field}
                  label={translate('user-profile.password.fields.current')}
                  fullWidth
                  required
                  error={Boolean(errors.currentPassword)}
                  helperText={errors.currentPassword?.message ?? ' '}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            <Controller
              name="newPassword"
              control={control}
              rules={{
                required: translate('user-profile.password.errors.newRequired'),
                minLength: {
                  value: PASSWORD_MIN_LENGTH,
                  message: translate('user-profile.password.errors.minLength', {
                    min: PASSWORD_MIN_LENGTH,
                  }),
                },
                pattern: {
                  value: PASSWORD_PATTERN,
                  message: translate('user-profile.password.errors.pattern'),
                },
              }}
              render={({ field }) => (
                <PasswordField
                  {...field}
                  label={translate('user-profile.password.fields.new')}
                  fullWidth
                  required
                  error={Boolean(errors.newPassword)}
                  helperText={errors.newPassword?.message ?? ' '}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            <Controller
              name="confirmPassword"
              control={control}
              rules={{
                required: translate('user-profile.password.errors.confirmRequired'),
                validate: (value) =>
                  value === newPassword ||
                  translate('user-profile.password.errors.mismatch'),
              }}
              render={({ field }) => (
                <PasswordField
                  {...field}
                  label={translate('user-profile.password.fields.confirm')}
                  fullWidth
                  required
                  error={Boolean(errors.confirmPassword)}
                  helperText={errors.confirmPassword?.message ?? ' '}
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            gap: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: { xs: 'stretch', sm: 'flex-end' },
            '& > .MuiButton-root': {
              width: { xs: '100%', sm: 'auto' },
              ml: { xs: '0 !important', sm: undefined },
            },
          }}
        >
          <Button
            onClick={handleClose}
            variant="contained"
            color="primary"
            startIcon={<CloseIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, minWidth: { xs: 0, sm: 120 } }}
          >
            {translate('ra.action.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            disabled={!isValid || submitting}
            sx={{ textTransform: 'none', fontWeight: 600, minWidth: { xs: 0, sm: 120 } }}
          >
            {translate('ra.action.save')}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default PasswordResetDialog;
